/**
 * Display formats for amounts and times.
 */

import { formatTokenAmount, YOUTUBE_TIME_ZONE, zonedDate } from "@morcast/campaigns";

/** "100,000 USDC" from base units and the token. Base units if the token is unknown. */
export function formatAmount(
  units: string,
  token: { symbol: string | null; decimals: number | null },
): string {
  if (token.decimals === null || token.symbol === null) {
    return `${units} base units`;
  }
  return `${formatTokenAmount(BigInt(units), token.decimals)} ${token.symbol}`;
}

/**
 * A count with thousands separators: "1,000,000". A decimal keeps its fraction, such as the
 * median of an even number of posts: "2,000.5".
 */
export function formatCount(value: string): string {
  const [whole = "0", fraction] = value.split(".");
  const grouped = BigInt(whole).toLocaleString("en-US");
  return fraction ? `${grouped}.${fraction}` : grouped;
}

/**
 * A length of time in the largest unit that reads naturally, rounded down: "under an hour",
 * "5 hours", "3 days".
 */
export function formatDuration(milliseconds: number): string {
  const hours = Math.floor(milliseconds / 3_600_000);
  if (hours < 1) {
    return "under an hour";
  }
  if (hours < 48) {
    return `${hours} hour${hours === 1 ? "" : "s"}`;
  }
  return `${Math.floor(hours / 24)} days`;
}

/**
 * How long ago an instant was, for a list of recent events: "just now", "5 minutes ago",
 * "3 hours ago", "2 days ago". After a week it is the date, such as "10 Oct 2026".
 */
export function formatAgo(iso: string, now: number = Date.now()): string {
  const minutes = Math.floor((now - Date.parse(iso)) / 60_000);
  if (minutes < 1) {
    return "just now";
  }
  if (minutes < 60) {
    return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  }
  const days = Math.floor(hours / 24);
  if (days < 7) {
    return `${days} day${days === 1 ? "" : "s"} ago`;
  }
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "UTC",
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(iso));
}

/** An instant in UTC, e.g. "10 Oct 2026, 00:00 UTC". */
export function formatUtc(iso: string): string {
  const formatted = new Intl.DateTimeFormat("en-GB", {
    timeZone: "UTC",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(new Date(iso));
  return `${formatted} UTC`;
}

/**
 * The campaign window in the platform's terms: UTC times for X, Los Angeles reporting dates for
 * YouTube, whose window ends at 00:00 on the day after the final date.
 */
export function formatWindow(platform: "X" | "YOUTUBE", startAt: string, endAt: string): string {
  if (platform === "X") {
    return `${formatUtc(startAt)} to ${formatUtc(endAt)}`;
  }
  const first = zonedDate(new Date(startAt), YOUTUBE_TIME_ZONE);
  const final = zonedDate(new Date(new Date(endAt).getTime() - 1), YOUTUBE_TIME_ZONE);
  return `${formatDate(first)} to ${formatDate(final)} (Los Angeles reporting dates)`;
}

/** "5 Oct 2026" from "2026-10-05". */
function formatDate(date: string): string {
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "UTC",
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(`${date}T00:00:00Z`));
}
